import { Trans, useLingui } from "@lingui/react/macro";
import type { DrokToolkit } from "@rakazo/contracts";
import { drokToolkitKey, isDrokToolkitSlug } from "@rakazo/contracts";
import {
  buildFeaturedConnectorTiles,
  CONNECTION_CATALOG_PAGE_SIZE,
  filterConnectionCatalogItems,
  managedAppFor,
  matchesWordPrefixes,
  PLUGIN_CATEGORIES,
} from "@rakazo/core";
import { Badge, Button, Input, Spinner } from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useMemo, useState } from "react";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { ConnectorIcon, usePluginCategoryLabel } from "../../pages/connection-catalog-ui";
import { drokRpc } from "../rpc";

interface ToolkitPickerProps {
  value: DrokToolkit[];
  onChange: (next: DrokToolkit[]) => void;
}

interface Tile {
  slug: string;
  name: string;
  logo?: string;
  connected: boolean;
  category: string | null;
}

/** Composio apps for a template: IT's connected ones first, then the full Composio catalog. */
export function ToolkitPicker({ value, onChange }: ToolkitPickerProps) {
  const { t } = useLingui();
  const categoryLabel = usePluginCategoryLabel();
  const [input, setInput] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(CONNECTION_CATALOG_PAGE_SIZE);
  const connected = useQuery({
    queryKey: ["drok", "admin.connectedToolkits"],
    queryFn: () => drokRpc.templates.connectedToolkits(),
  });
  const catalog = useQuery({
    queryKey: ["drok", "admin.toolkitCatalog"],
    queryFn: () => rpc.connections.catalog({ connectorId: "composio" }),
  });
  // Categories come from the plugin marketplace; without it the picker just has no category chips.
  const marketplace = useQuery({
    queryKey: ["drok", "admin.toolkitCategories"],
    queryFn: () => rpc.marketplace.catalog({ query: "" }),
    retry: false,
    staleTime: 60_000,
  });
  const chosen = new Set(value.map((toolkit) => drokToolkitKey(toolkit.slug)));
  const needle = input.trim();

  const tiles = useMemo(() => {
    const items = catalog.data ?? [];
    const connectedRows = connected.data ?? [];
    const connectedKeys = new Set(connectedRows.map((row) => drokToolkitKey(row.slug)));
    const categoryByKey = new Map<string, string>();
    for (const entry of marketplace.data?.entries ?? []) {
      const app = entry.category ? managedAppFor(entry, items) : undefined;
      if (app && entry.category) categoryByKey.set(drokToolkitKey(app.slug), entry.category);
    }
    const featured = new Set(
      buildFeaturedConnectorTiles(items).flatMap((tile) =>
        tile.item ? [drokToolkitKey(tile.item.slug)] : [],
      ),
    );
    const matched = needle ? filterConnectionCatalogItems(items, needle) : items;
    const lower = needle.toLowerCase();
    const extraConnected = connectedRows.filter(
      (row) =>
        (!lower ||
          row.name.toLowerCase().includes(lower) ||
          row.slug.toLowerCase().includes(lower)) &&
        !items.some((item) => drokToolkitKey(item.slug) === drokToolkitKey(row.slug)),
    );
    const all: Tile[] = [
      ...extraConnected.map((row) => ({
        slug: row.slug,
        name: row.name,
        connected: true,
        category: categoryByKey.get(drokToolkitKey(row.slug)) ?? null,
      })),
      ...matched.map((item) => ({
        slug: item.slug,
        name: item.name,
        ...(item.logo ? { logo: item.logo } : {}),
        connected: item.connected || connectedKeys.has(drokToolkitKey(item.slug)),
        category: categoryByKey.get(drokToolkitKey(item.slug)) ?? null,
      })),
    ];
    // Connected first, then name-prefix matches (or the featured apps on an empty query).
    const rank = (tile: Tile) =>
      (tile.connected ? 0 : 4) +
      (needle
        ? matchesWordPrefixes(tile.name, needle)
          ? 0
          : 2
        : featured.has(drokToolkitKey(tile.slug))
          ? 0
          : 2);
    return all
      .map((tile, index) => ({ tile, index }))
      .sort((a, b) => rank(a.tile) - rank(b.tile) || a.index - b.index)
      .map(({ tile }) => tile);
  }, [catalog.data, connected.data, marketplace.data, needle]);

  const logoByKey = new Map(tiles.map((tile) => [drokToolkitKey(tile.slug), tile.logo]));
  const categories = PLUGIN_CATEGORIES.filter((name) =>
    (catalog.data ? tiles : []).some((tile) => tile.category === name),
  );
  const shown = category ? tiles.filter((tile) => tile.category === category) : tiles;

  const toggle = (tile: Tile) => {
    if (chosen.has(drokToolkitKey(tile.slug))) {
      remove(tile.slug);
    } else {
      onChange([
        ...value,
        { slug: tile.slug, name: tile.name, ...(tile.logo ? { logo: tile.logo } : {}) },
      ]);
    }
  };
  function remove(slug: string) {
    onChange(value.filter((toolkit) => drokToolkitKey(toolkit.slug) !== drokToolkitKey(slug)));
  }

  // Without a Composio key the catalog is empty or fails, so IT can type a slug such as gmail instead.
  const typedSlug = drokToolkitKey(input);
  const typedMatch = tiles.find((tile) => drokToolkitKey(tile.slug) === typedSlug);
  const canAddTyped = isDrokToolkitSlug(typedSlug) && !chosen.has(typedSlug);
  const addTyped = () => {
    if (typedMatch) toggleOn(typedMatch);
    else if (canAddTyped) onChange([...value, { slug: typedSlug, name: typedSlug }]);
    else return;
    setInput("");
  };
  function toggleOn(tile: Tile) {
    if (!chosen.has(drokToolkitKey(tile.slug))) toggle(tile);
  }
  const catalogUnavailable = catalog.isError || (catalog.isSuccess && catalog.data.length === 0);
  return (
    <div className="space-y-2" data-testid="toolkit-picker">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map((toolkit) => {
            const logo = toolkit.logo ?? logoByKey.get(drokToolkitKey(toolkit.slug));
            return (
              <Badge key={toolkit.slug} variant="secondary" className="gap-1">
                <ConnectorIcon name={toolkit.name} logo={logo} className="size-4 rounded" />
                {toolkit.name}
                <button
                  type="button"
                  aria-label={t`Remove ${toolkit.name}`}
                  className="rounded px-1 focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => remove(toolkit.slug)}
                >
                  ×
                </button>
              </Badge>
            );
          })}
        </div>
      ) : null}
      <Input
        type="search"
        aria-label={t`Search apps`}
        placeholder={t`Search apps`}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setVisibleCount(CONNECTION_CATALOG_PAGE_SIZE);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
          // Enter must not submit the surrounding publish form.
          e.preventDefault();
          addTyped();
        }}
      />
      {categories.length >= 2 ? (
        <div className="flex flex-wrap gap-2" data-testid="toolkit-categories">
          {[null, ...categories].map((name) => (
            <Button
              key={name ?? "all"}
              type="button"
              size="sm"
              variant={category === name ? "default" : "secondary"}
              aria-pressed={category === name}
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => {
                setCategory(name);
                setVisibleCount(CONNECTION_CATALOG_PAGE_SIZE);
              }}
            >
              {name ? categoryLabel(name) : <Trans>All</Trans>}
            </Button>
          ))}
        </div>
      ) : null}
      {connected.isPending || catalog.isFetching ? <Spinner aria-label={t`Loading…`} /> : null}
      {connected.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(connected.error)}
        </p>
      ) : null}
      {catalog.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(catalog.error)}
        </p>
      ) : null}
      {canAddTyped && !typedMatch ? (
        <Button type="button" size="sm" variant="secondary" onClick={addTyped}>
          <Trans>Add “{typedSlug}”</Trans>
        </Button>
      ) : null}
      {catalogUnavailable ? (
        <p className="text-xs text-muted-foreground" data-testid="toolkit-catalog-hint">
          <Trans>
            The app list needs a Composio key. You can type an app slug, like gmail, and press
            Enter.
          </Trans>
        </p>
      ) : null}
      {shown.length > 0 ? (
        <>
          <ul
            aria-label={t`Apps`}
            className="grid max-h-80 grid-cols-1 gap-1 overflow-y-auto overflow-x-hidden rounded-md border border-border p-1 sm:grid-cols-2"
          >
            {shown.slice(0, visibleCount).map((tile) => {
              const selected = chosen.has(drokToolkitKey(tile.slug));
              return (
                <li key={tile.slug} className="min-w-0">
                  <button
                    type="button"
                    aria-pressed={selected}
                    className={`flex w-full min-w-0 items-center gap-2 rounded-lg border px-2 py-1.5 text-start text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring ${selected ? "border-primary bg-muted" : "border-transparent"}`}
                    onClick={() => toggle(tile)}
                  >
                    <ConnectorIcon name={tile.name} logo={tile.logo} className="size-8" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">
                        {tile.name}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {tile.slug}
                      </span>
                    </span>
                    {tile.connected ? (
                      <Badge variant="secondary" className="shrink-0">
                        <Trans>Connected</Trans>
                      </Badge>
                    ) : null}
                    {selected ? <Check aria-hidden className="size-4 shrink-0" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {shown.length > visibleCount ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setVisibleCount((count) => count + CONNECTION_CATALOG_PAGE_SIZE)}
            >
              <Trans>Show more</Trans>
            </Button>
          ) : null}
        </>
      ) : connected.isSuccess && !catalog.isFetching ? (
        <p className="text-xs text-muted-foreground">
          <Trans>No apps found.</Trans>
        </p>
      ) : null}
    </div>
  );
}
